/**
 * Round stone tower — the world's landmark, and the one building whose HEIGHT is drawn from the seed
 * (2-4 floors) rather than the variant. See buildingSmall.ts for the shape of this file.
 *
 * THE LADDER (6-6.5 m across; 10-16 m tall roofed, 4.5-10.5 m as a battlement). Being tall and thin,
 * a tower reads as a vertical MARK long after a house has become a dot, which is exactly what a
 * landmark should do at range:
 *   lod 0   0.25 m  hollow shell, spiral ramp, floor landings, arrow-slits, roof or crenellations
 *   lod 1   0.5 m   plinth + shaft drum + roof cone (12-13 voxels across, 36-130 rows tall)
 *   lod 2   1 m     the same three parts (6-7 across)
 *   lod 3   2 m     plinth folded into the shaft; 9-32 rows still resolve the cone
 *   lod 4   4 m     one column at the shaft's height plus a roof-coloured cap — 1.6 voxels across
 *   lod 5   8 m     one column to the top
 *   lod 6+          nothing (see registry.columnDot)
 *
 * THE CRENELLATIONS STOP AT LOD 0. A merlon is 1.2 m of arc on a 2 m wall ring — the RING is one
 * coarse voxel thick at lod 1 and sub-voxel above it, so a faithful parapet is a shape the lattice
 * cannot hold. The stand-in continues the shaft to the parapet top instead: same silhouette height,
 * same material, no ring of one-voxel spikes.
 */
import { mat } from '../../../materials/index.js';
import { sdfBoxS, sdfCylinderS } from '../../../voxel/shapes.js';
import { blockHalf, buildingSink, roofConeBase, roofTipRadius, } from '../coarseModel.js';
import { conePart, cylinderPart, } from '../../../voxel/buildParts.js';
import { StampType } from '../StampTypes.js';
import { makeRng } from '../../../util/rng.js';
import { MAX_FOLD_LOD, MAX_SCATTER_LOD } from '../lodLevels.js';
import { SDF_THRESHOLD, TWO_PI, pushLamp, pushTorchAt, rotateInv } from '../buildingCommon.js';
function params(variant, seed) {
    // Per-building RNG: number of floors is the random 2-4 the tower height varies over.
    // (Deriving floors from `seed` is safe even though bounds are computed with seed=0 in
    // stampAffectsChunk: that culling is X/Z only, and the X/Z footprint depends on `radius`
    // which is variant-derived, so the horizontal bounds are identical regardless of floors.)
    const rng = makeRng(seed);
    const numFloors = 2 + Math.floor(rng() * 3); // 2, 3, or 4
    const battlement = variant >= 2; // variants 2-3: crenellated top instead of a roof
    const radius = 12 + (variant % 2); // 12-13 voxels (~3-3.25m); wide enough for a spiral band + a central landing
    const wallThickness = 2;
    const floorHeight = 12; // voxels per floor (~3m)
    const interiorR = radius - wallThickness;
    const stairWidth = 5; // width of the wall-hugging spiral band (voxels)
    const topFloorY = 1 + (numFloors - 1) * floorHeight;
    const roofHeight = radius + 4;
    const parapetH = 5;
    // Roofed towers keep a top room + ceiling above the top floor; battlements stop at the terrace.
    const wallHeight = battlement ? topFloorY : numFloors * floorHeight;
    // Stone / brick theme (visually distinct from the wooden hut).
    const wallMaterials = [mat('stone'), mat('brick2')];
    const roofMaterials = [mat('roof'), mat('roof2')];
    const torchRoll = rng();
    return {
        radius,
        wallHeight,
        roofHeight,
        wallThickness,
        doorHalfWidth: 3,
        doorHeight: 8,
        numFloors,
        floorHeight,
        interiorR,
        innerEdge: interiorR - stairWidth, // ramp occupies [innerEdge, interiorR]; landings fill r <= innerEdge
        rampThickness: 2.5,
        platThickness: 2,
        windowHalfHeight: 3,
        windowHalfAngle: 0.2, // narrow arrow-slit windows
        topFloorY,
        stairArc: Math.PI, // final flight climbs over 180deg; the top deck fills the rest of the ring
        battlement,
        parapetH,
        merlonSectors: 16, // 8 merlons / 8 crenels around the parapet
        wallMaterial: wallMaterials[variant % wallMaterials.length],
        foundationMaterial: mat('stone2'),
        floorMaterial: mat('cobble2'),
        roofMaterial: roofMaterials[variant % roofMaterials.length],
        torchRoll,
    };
}
/** LOD 0 — the original generateTowerSDF body, reading its numbers from params. */
function lod0(p, _variant, _seed, rotation) {
    const voxels = [];
    const { radius, battlement, topFloorY, parapetH, wallHeight, roofHeight } = p;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    // Bounding box with margin for rotation
    const maxDim = radius + 5;
    const maxHeight = (battlement ? topFloorY + parapetH : wallHeight + roofHeight) + 2;
    // Sample SDF over the entire bounding volume
    for (let y = -2; y <= maxHeight; y++) {
        for (let x = -maxDim; x <= maxDim; x++) {
            for (let z = -maxDim; z <= maxDim; z++) {
                // Rotate query point to local building space (inverse rotation)
                const { x: rx, z: rz } = rotateInv(x, z, cos, sin);
                const hit = evaluateTowerSDF(rx, y, rz, p);
                if (hit) {
                    voxels.push({ x, y, z, material: SAMPLE.material, weight: SAMPLE.weight });
                }
            }
        }
    }
    // --- Decorations (deterministic per building) ---
    // The roll comes from params: the floor COUNT is drawn off the same stream first, so re-opening
    // makeRng(seed) here would hand the torches the floors' draw.
    // Exterior torches flanking the door (front = -Z) on 40% of towers.
    if (p.torchRoll < 0.4) {
        pushTorchAt(voxels, -(p.doorHalfWidth + 1), -radius, p.doorHeight, cos, sin);
        pushTorchAt(voxels, p.doorHalfWidth + 1, -radius, p.doorHeight, cos, sin);
    }
    return voxels;
}
/** Top of the LOD-0 silhouette: the roof apex, or a battlement's parapet. */
function topFine(p) {
    return p.battlement ? p.topFloorY + p.parapetH : p.wallHeight + p.roofHeight;
}
function lodParts(p, lod) {
    const stride = 1 << lod;
    const top = topFine(p);
    const sink = buildingSink(p.radius, stride);
    if (lod <= 3) {
        const parts = [];
        if (!p.battlement) {
            // Roof first, and the shaft below runs one stride past the eave to overlap it — see
            // buildingHut.lodParts for why the roof is not the part that gets sunk.
            const tipR = roofTipRadius(p.radius + 1, p.wallHeight, top, stride);
            parts.push(conePart(roofConeBase(p.radius + 1, p.radius, tipR, p.wallHeight, top, stride), tipR, p.wallHeight, top, p.roofMaterial));
        }
        if (lod <= 2)
            parts.push(cylinderPart(p.radius + 1, sink + 1, p.foundationMaterial, -sink));
        // A battlement's parapet is the same radius as the shaft it stands on, so shaft and parapet are
        // ONE drum to the top; a roofed tower's shaft stops a stride above its eave and the cone takes over.
        const shaftTop = p.battlement ? top : p.wallHeight + stride;
        parts.push(cylinderPart(p.radius, shaftTop + sink, p.wallMaterial, -sink));
        pushLamp(parts, hasLamp(p), p.doorHeight, p.radius, lod, stride);
        return parts;
    }
    // lod 4-5: ONE BLOB, plus one voxel of light beside it if this building is a lit one — the trees'
    // answer at these strides, with one difference that matters. A tree is genuinely narrower than a
    // lod-4 voxel, so its blob is a single column; a 12 m house is three voxels wide, and drawing it as
    // one would have it swell to three times its size as you cross into lod 3. `blockHalf` sizes the blob
    // to the building and converges on a single voxel by lod 5 on its own.
    //
    // (The two-part pin this replaced put a roof-coloured cap on the top row. At these strides that is a
    // second voxel spent on a distinction nobody can resolve.)
    const blob = [cylinderPart(blockHalf(p.radius, stride), top + sink, p.battlement ? p.wallMaterial : p.roofMaterial, -sink)];
    pushLamp(blob, hasLamp(p), p.doorHeight, p.radius, lod, stride);
    return blob;
}
/**
 * Whether this instance carries exterior torches — LOD 0's own roll, read in one place because the
 * coarse cache keys on it (see coarseSeedClass). The FRACTION is this building's own decision; what
 * a lamp then IS at a coarse level is shared (buildingCommon.pushLamp).
 */
const LAMP_CHANCE = 0.4;
function hasLamp(p) {
    return p.torchRoll < LAMP_CHANCE;
}
export const BUILDING_TOWER_SPEC = {
    type: StampType.BUILDING_TOWER,
    maxLod: MAX_SCATTER_LOD,
    foldLod: MAX_FOLD_LOD,
    params,
    lod0,
    lodParts,
    // The floor count is the only seed-derived DIMENSION in the world, so it is also the only coarse
    // shape class: three of them, against one cache entry per instance if the seed were the key.
    // Two seed-derived facts reach the coarse model: the floor count (its HEIGHT) and the torch roll
    // (whether it carries a lamp). Both go in the class, or the cache serves one instance another's
    // model — a lit tower where an unlit one stands, or a two-floor tower's height on a four-floor one.
    coarseSeedClass: (seed) => {
        const p = params(0, seed);
        return p.numFloors * 2 + (hasLamp(p) ? 1 : 0);
    },
};
/**
 * The evaluator's result, as a scratch rather than a return object: it is read ~95k times per placed
 * building (the sample loop in lod0), and a `{ material, weight }` per hit was the bulk of that cost.
 * Written on `true`, undefined on `false`.
 */
const SAMPLE = { material: 0, weight: 0 };
/**
 * Evaluate the tower at a point in local (unrotated) space.
 * Branch order matters: solid interior features (ground floor, central landings, top deck,
 * ramp) are tested before the interior air carve so they survive; the air carve hollows the
 * rest. Roofed vs battlement differ only at the top (ceiling + cone vs parapet + crenels).
 */
function evaluateTowerSDF(x, y, z, p) {
    const { radius, wallHeight, roofHeight, wallThickness, doorHalfWidth, doorHeight, numFloors, floorHeight, interiorR, innerEdge, rampThickness, platThickness, windowHalfHeight, windowHalfAngle, topFloorY, stairArc, battlement, parapetH, merlonSectors, wallMaterial, foundationMaterial, floorMaterial, roofMaterial, } = p;
    // Foundation SDF (-2 to 0) + exterior entrance stairs (front, -Z)
    if (y >= -2 && y < 0) {
        const foundationDist = sdfCylinderS(x, y + 1, z, radius + 1, 1);
        if (foundationDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = foundationMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
        const stairStep = -y - 1;
        if (stairStep >= 0 && stairStep < 4) {
            const stairZ = -radius - 1.5 - stairStep;
            const stairDist = sdfBoxS(x, 0, z - stairZ, doorHalfWidth, 0.5, 0.5);
            if (stairDist < SDF_THRESHOLD) {
                {
                    SAMPLE.material = foundationMaterial;
                    SAMPLE.weight = 0.45;
                    return true;
                }
            }
        }
    }
    // Doorway carve - through the wall and slightly in front to clear terrain
    if (y >= 0 && y < doorHeight) {
        const doorwayDist = sdfBoxS(x, y - doorHeight / 2, z + radius + 1, doorHalfWidth + 0.5, doorHeight / 2, wallThickness + 2);
        if (doorwayDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = 0;
                SAMPLE.weight = -0.5;
                return true;
            }
        }
    }
    // Ground floor (solid disc, y = 0)
    if (y >= 0 && y < 1) {
        const floorDist = sdfCylinderS(x, y - 0.5, z, radius, 0.5);
        if (floorDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = floorMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Interior features share polar coords. rampAng is offset so the spiral's base seam
    // (where each flight starts) sits over the door (-Z) - you climb straight from the entrance.
    const r = Math.sqrt(x * x + z * z);
    let physAng = Math.atan2(z, x);
    if (physAng < 0)
        physAng += TWO_PI;
    let rampAng = physAng + Math.PI / 2; // door at -Z (physAng 3PI/2) -> rampAng 0
    if (rampAng >= TWO_PI)
        rampAng -= TWO_PI;
    // Central floor landings for the LOWER floors: a disc filling only the centre (r <= innerEdge),
    // so nothing overhangs the ramp band. The top floor is a full deck (handled next).
    for (let f = 1; f < numFloors - 1; f++) {
        const surfaceY = 1 + f * floorHeight;
        if (y >= surfaceY - platThickness && y < surfaceY && r <= innerEdge) {
            {
                SAMPLE.material = floorMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Top floor deck: a full disc, minus a stairwell opening in the outer ring where the final
    // flight climbs (rampAng <= stairArc). Because that flight is confined to the arc, the deck is
    // solid everywhere else without capping the climb - the top floor is a complete floor / terrace.
    if (y >= topFloorY - platThickness && y < topFloorY && r <= interiorR) {
        const inStairwell = r >= innerEdge && rampAng <= stairArc;
        if (!inStairwell) {
            {
                SAMPLE.material = floorMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Spiral ramp (helicoid): a wall-hugging band (r in [innerEdge, interiorR]). Lower flights make
    // one full turn per floor; the FINAL flight is confined to stairArc (a little steeper) so the top
    // deck can close over the rest of the ring. Clearance above a tread is floorHeight - rampThickness.
    if (y >= 1 && r >= innerEdge && r <= interiorR) {
        for (let k = 0; k < numFloors - 1; k++) {
            let hk;
            if (k === numFloors - 2) { // final flight -> confined to the stairwell arc
                if (rampAng > stairArc)
                    continue;
                hk = 1 + k * floorHeight + (rampAng / stairArc) * floorHeight;
            }
            else {
                hk = 1 + k * floorHeight + (rampAng / TWO_PI) * floorHeight;
            }
            if (y <= hk && y > hk - rampThickness) {
                {
                    SAMPLE.material = floorMaterial;
                    SAMPLE.weight = 0.45;
                    return true;
                }
            }
        }
    }
    // Interior air - hollow out everything else inside the shell (up to the ceiling / top deck)
    if (y >= 1 && y < wallHeight - 1) {
        const interiorDist = sdfCylinderS(x, y - wallHeight / 2, z, interiorR, wallHeight / 2);
        if (interiorDist < -SDF_THRESHOLD) {
            {
                SAMPLE.material = 0;
                SAMPLE.weight = -0.5;
                return true;
            }
        }
    }
    // Ceiling under the roof (roofed variants only; battlements are open to the terrace)
    if (!battlement && y >= wallHeight - 2 && y < wallHeight) {
        const ceilingDist = sdfCylinderS(x, y - wallHeight + 1, z, interiorR, 1);
        if (ceilingDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = mat('concrete');
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Walls (hollow cylinder) with a door opening and windows on every floor
    if (y >= 1 && y < wallHeight) {
        const outerDist = sdfCylinderS(x, y - wallHeight / 2, z, radius, wallHeight / 2);
        const innerDist = sdfCylinderS(x, y - wallHeight / 2, z, radius - wallThickness, wallHeight / 2 + 1);
        if (outerDist < SDF_THRESHOLD && innerDist > -SDF_THRESHOLD) {
            // Door opening (front, -Z)
            if (z < -radius + wallThickness + SDF_THRESHOLD && y < doorHeight) {
                const doorDist = sdfBoxS(x, y - doorHeight / 2, z + radius, doorHalfWidth, doorHeight / 2, wallThickness + 1);
                if (doorDist < SDF_THRESHOLD) {
                    return false;
                }
            }
            // Windows: 4 arrow-slits per floor at 45/135/225/315deg (clear of the door at -Z). A
            // battlement's open terrace (its top floor) gets none.
            const windowFloors = battlement ? numFloors - 1 : numFloors;
            for (let f = 0; f < windowFloors; f++) {
                const yC = 1 + f * floorHeight + floorHeight / 2;
                if (Math.abs(y - yC) <= windowHalfHeight) {
                    for (let w = 0; w < 4; w++) {
                        const aC = Math.PI / 4 + w * (Math.PI / 2);
                        let dA = Math.abs(physAng - aC);
                        if (dA > Math.PI)
                            dA = TWO_PI - dA;
                        if (dA < windowHalfAngle) {
                            {
                                SAMPLE.material = 0;
                                SAMPLE.weight = -0.5;
                                return true;
                            }
                        }
                    }
                }
            }
            {
                SAMPLE.material = wallMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Conical roof (roofed variants)
    if (!battlement && y >= wallHeight && y < wallHeight + roofHeight) {
        const roofY = y - wallHeight;
        const roofRadius = radius + 1 - (roofY * (radius + 1) / roofHeight);
        const roofDist = sdfCylinderS(x, 0, z, roofRadius, 0.5);
        if (roofDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = roofMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Crenellated parapet (battlement variants): a wall ring rising above the terrace. Solid all
    // round at the base, then only alternating merlon sectors above, with crenel gaps between them.
    // Test the ring with r directly (a short sdfCylinder's vertical term would swamp the radial one).
    if (battlement && y >= topFloorY && y < topFloorY + parapetH &&
        r >= interiorR - SDF_THRESHOLD && r <= radius + SDF_THRESHOLD) {
        if (y < topFloorY + 2) {
            {
                SAMPLE.material = wallMaterial;
                SAMPLE.weight = 0.45;
                return true;
            } // solid parapet base
        }
        const sector = Math.floor((physAng / TWO_PI) * merlonSectors);
        if (sector % 2 === 0) {
            {
                SAMPLE.material = wallMaterial;
                SAMPLE.weight = 0.45;
                return true;
            } // merlon (crenels are the gaps)
        }
    }
    return false;
}
//# sourceMappingURL=buildingTower.js.map